import { useRef, useCallback } from 'react';

/**
 * Hook to synchronize horizontal scroll between two scrollable elements
 * Useful for keeping sticky headers in sync with scrollable content
 *
 * @returns {Object} Object containing refs and scroll handlers
 * @returns {React.RefObject} stickyRef - Ref for the sticky element
 * @returns {React.RefObject} contentRef - Ref for the content element
 * @returns {Function} syncFromSticky - Handler to sync from sticky to content
 * @returns {Function} syncFromContent - Handler to sync from content to sticky
 *
 * @example
 * const { stickyRef, contentRef, syncFromSticky, syncFromContent } = useScrollSync();
 *
 * <div ref={stickyRef} onScroll={syncFromSticky}>Sticky Header</div>
 * <div ref={contentRef} onScroll={syncFromContent}>Content</div>
 */
export const useScrollSync = () => {
    const stickyRef = useRef(null);
    const contentRef = useRef(null);
    const isSyncingRef = useRef(false);

    const syncFromSticky = useCallback(() => {
        if (isSyncingRef.current || !stickyRef.current || !contentRef.current) {
            return;
        }

        isSyncingRef.current = true;
        contentRef.current.scrollLeft = stickyRef.current.scrollLeft;
        requestAnimationFrame(() => {
            isSyncingRef.current = false;
        });
    }, []);

    const syncFromContent = useCallback(() => {
        if (isSyncingRef.current || !stickyRef.current || !contentRef.current) {
            return;
        }

        isSyncingRef.current = true;
        stickyRef.current.scrollLeft = contentRef.current.scrollLeft;
        requestAnimationFrame(() => {
            isSyncingRef.current = false;
        });
    }, []);

    return {
        stickyRef,
        contentRef,
        syncFromSticky,
        syncFromContent
    };
};

export default useScrollSync;
